import { useMemo, useState } from 'react';

import styled from 'styled-components';

import { createForm, useCreateFormStore, useFormContext } from '@components/form';
import { FormInstance } from '@components/form/create-form';
import { HideContainer } from '@components/hide-container';
import { IconSwap } from '@components/icons/swap';
import { SelectOption } from '@components/select';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingGroup, VariableValue } from '@components/text-editors/addons/field-mapping';
import {
  getScopedTranslate,
  styledTranslate,
  TranslationMap,
  useScopedTranslate,
} from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { Font } from '@styles/theme';
import { uuid } from '@utils/uuid';

import {
  ConditionComparator,
  JourneyActionDetailed,
  JourneyType,
  MappableVariable,
  TravellerJourneyActionDetailed,
} from 'api/types';
import {
  ActionFooter,
  DescriptionFormField,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';

import { BaseContentProps } from '../common';
import { getComparatorLabel, getValueLabel } from '../condition-v2/description';
import { getFieldMappingId } from '../helpers';
import { ComparatorComponent } from '../shared/comparator-component';
import { filterSelectAttributeVariables } from '../shared/field-utils';

export type Data = {
  description: string;
  field: VariableValue;
  comparator: ConditionComparator;
  value: any;
};

interface ConditionState {
  id: string;
  field: VariableValue;
  comparator?: ConditionComparator;
  value?: any;
}

const Form = createForm<Data>();

type ActionType = 'wait_for_attribute_change';

type WaitForAttributeChangeFormContentProps = BaseContentProps<
  JourneyActionDetailed<ActionType> | TravellerJourneyActionDetailed<ActionType>
> & {
  onSubmit(data: {
    field: VariableValue;
    comparator: ConditionComparator;
    value?: any;
    description?: string;
  }): Promise<any>;
  onCancel(): void;
  journeyId?: JourneyType['shortId'];
  isTraveller?: boolean;
};

export const filterAttributeVariables = (groups: FieldMappingGroup[]): FieldMappingGroup[] => {
  return groups
    .map((group) => {
      if (group.id !== 'traveller') {
        return null;
      }

      const filteredFields = filterSelectAttributeVariables(group.fields);

      return {
        ...group,
        fields: filteredFields.filter(
          (field) => field.source?.includes('attribute') && field.parentType !== 'user'
        ),
      };
    })
    .filter((group): group is FieldMappingGroup => group !== null);
};

type GetDescriptionProps<Styled extends boolean = true> = {
  values: any;
  styled?: Styled;
  translations: TranslationMap;
};

export function getDescriptionText(props: GetDescriptionProps<false>): string;
export function getDescriptionText(props: GetDescriptionProps<true>): JSX.Element;
export function getDescriptionText({
  values,
  styled = true,
  translations,
}: GetDescriptionProps<boolean>): JSX.Element | string {
  const { t } = getScopedTranslate(translations);

  const field = values?.field;
  const comparator = values?.comparator;
  const value = values?.value;

  if (!field || !comparator) {
    return t('configureAction');
  }

  const valueLabel = getValueLabel(value, comparator);
  const fieldLabel =
    field?.variables && (Object.values(field.variables)[0] as MappableVariable)?.label;

  if (!fieldLabel) {
    return t('configureAction');
  }

  const comparatorString =
    comparator === 'changes_value'
      ? t('changesValue')
      : getComparatorLabel(comparator) || comparator;

  if (styled) {
    const commonProps = { font: 'regular14' as Font, style: { display: 'inline' } };
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {t('continueWhen')}
        <ConditionContainer>
          {styledTranslate({
            key: 'conditionDescription',
            variables: {
              field: {
                text: fieldLabel,
                color: 'elementBlue100',
                ...commonProps,
              },
              comparator: {
                text: comparatorString,
                color: 'textSecondary',
                ...commonProps,
              },
              value: {
                text: valueLabel,
                color: 'textSecondary',
                ...commonProps,
              },
            },
            translations,
          })}
        </ConditionContainer>
      </div>
    );
  } else {
    return t('continueWhenNoStyle', {
      field: fieldLabel,
      comparator: comparatorString,
      value: valueLabel,
    });
  }
}

export const useDescription = (values: any, translations: TranslationMap) => {
  const descriptionText = useMemo(() => {
    return getDescriptionText({ values, styled: true, translations });
  }, [values, translations]);

  return { descriptionText };
};

export const WaitForAttributeChangeFormContent = ({
  action,
  onSubmit,
  onCancel,
  journeyId = '',
  isTraveller = false,
}: WaitForAttributeChangeFormContentProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();

  const fieldMappingId = getFieldMappingId(journeyId);

  const baseVariableGroups = useVariableGroups({
    actionVariables: action?.actionVariables,
    journeyId,
    types: [
      'text',
      'paragraph',
      'phone',
      'date',
      'boolean',
      'file',
      'file_multi',
      'select',
      'multi_select',
      'number',
      'decimal',
      'email',
    ],
  });

  const attributeVariableGroups = filterAttributeVariables(baseVariableGroups);

  const initialCondition: ConditionState = {
    id: uuid(),
    field: action.values?.field ?? { value: '' },
    comparator: action.values?.comparator as ConditionComparator,
    value: action.values?.value,
  };

  const [condition, setCondition] = useState<ConditionState>(initialCondition);
  const isDirty = formStore.useIsDirty();

  const formValues = formStore.useFormValues();

  const handleSubmit = () => {
    const field = formValues.field;
    const comparator = formValues.comparator;
    const value = formValues.value;
    const description = formValues.description;

    if (!field) {
      throw new Error('No attribute selected');
    }

    const submissionData = {
      field,
      comparator,
      value,
      description,
    } as Data;

    return onSubmit(submissionData);
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSubmit}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('fieldCondition')}>
          <Stack space={4}>
            <Text font="regular14" text={t('actionDescription')} />
            <SingleConditionComponent
              variableGroups={attributeVariableGroups}
              condition={condition}
              setCondition={setCondition}
              form={Form}
              fieldMappingId={fieldMappingId}
            />
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
      </Stack>
      <ActionFooter action={action} isDisabled={!isDirty} onCancel={onCancel} form={Form} />
    </FormWithUnsavedChangesPrompt>
  );
};

type SingleConditionComponentProps = {
  variableGroups: FieldMappingGroup[];
  condition: ConditionState;
  setCondition: (condition: ConditionState) => void;
  form: FormInstance<Data>;
  fieldMappingId: string;
};

const SingleConditionComponent = ({
  variableGroups,
  condition,
  setCondition,
  form: Form,
  fieldMappingId,
}: SingleConditionComponentProps) => {
  const { t } = useScopedTranslate(locale);
  const { store } = useFormContext();
  const element = store.useFieldValue('field') as VariableValue;

  const handleChangeFieldMapping = () => {
    setCondition({
      ...condition,
      comparator: undefined,
      value: undefined,
      field: { value: '' },
    });
  };

  return (
    <Stack space={2}>
      <Form.FieldMappingField
        id="field"
        label={t('attributeLabel')}
        variableGroups={variableGroups}
        defaultValue={condition.field}
        validators={['required']}
        singleLine
        plaintext
        onChange={handleChangeFieldMapping}
        fieldMappingId={fieldMappingId}
      />
      {(element?.value || condition.field?.value) && (
        <Comparator
          key={element?.value || condition.field?.value || 'comparator'}
          form={Form}
          condition={condition}
          variableGroups={variableGroups}
        />
      )}
    </Stack>
  );
};

const Comparator = ({
  condition,
  form: Form,
  variableGroups,
}: {
  condition: ConditionState;
  form: FormInstance<Data>;
  variableGroups: FieldMappingGroup[];
}) => {
  const { t } = useScopedTranslate(locale);

  const CHANGES_VALUE_COMPARATOR: SelectOption = {
    value: 'changes_value',
    label: (
      <Stack orientation="horizontal" space={3}>
        <IconSwap />
        {t('changesValue')}
      </Stack>
    ),
  };

  return (
    <ComparatorComponent
      condition={condition}
      form={Form}
      variableGroups={variableGroups}
      additionalComparators={[CHANGES_VALUE_COMPARATOR]}
    />
  );
};

const locale = {
  en: {
    actionDetails: 'Action details',
    actionDescription:
      'Once this action is active, it will wait until the selected field meets the specified conditions',
    fieldCondition: 'Field condition',
    attributeLabel: 'Field',
    comparatorLabel: 'Comparator',
    valueLabel: 'Value',
    valuesLabel: 'Values',
    dateLabel: 'Date',
    changesValue: 'Changes value',
    continueWhen: 'Continue when',
    configureAction: 'Configure action',
  },
};

const ConditionContainer = styled.div({
  ...lineClamp('-webkit-inline-box', 1, true),
});
